import _axios, { AxiosError, AxiosInstance } from "axios";
import VueRouter from "vue-router";

import {
  BiometricLoginResult,
  BiometricSecret,
  ClientSettings,
  DeviceInfo,
  IdnAndClients,
  LoginResult,
  MyPatientsList,
  PasswordRules,
  PatientDetailsViewModel,
  PatientListViewModel,
  PushNotificationInfo,
  TrustedDevice,
  TrustedDeviceSecret,
  UpdatePhoneOtpResponse,
  UpdateUserResponse,
  UserProfile,
  VerificationMethod,
  VerifyCodeResponse
} from "@/models";
import { isAndroid } from "@/plugins/native-app/android";
import { Auth } from "./auth";
import { RegexConnectionError } from "./regexPatterns";

export interface Gateway {
  login(email: string, password: string, trustedDeviceSecret?: string): Promise<LoginResult>;
  loginWithBiometrics(email: string, secret: string): Promise<BiometricLoginResult>;
  loginWith2FA(verificationCode: string): Promise<VerifyCodeResponse>;
  requestVerificationCode(method: VerificationMethod): Promise<void>;
  logout(): Promise<void>;

  getPasswordRules(): Promise<PasswordRules>;
  changePassword(current: string, password: string, confirm: string): Promise<void>;

  pingConnection(): Promise<void>;
  getForgotPasswordUrl(): Promise<string>;
  getIdnsAndClients(): Promise<IdnAndClients[]>;
  getAllPatients(): Promise<PatientListViewModel[]>;
  getMyPatients(): Promise<MyPatientsList>;
  getPatient(patientId: number): Promise<PatientDetailsViewModel>;
  subcribeToPatients(patientIds: number[], pushInfo?: PushNotificationInfo): Promise<void>;
  changeClient(newClientId: number, pushInfo?: PushNotificationInfo): Promise<void>;
  getClientSettings(clientId: number): Promise<ClientSettings>;

  /** This may be needed if a user is still logged in and their device token is refreshed */
  updatePushInfo(pushInfo: PushNotificationInfo): Promise<void>;

  getTrustedDevices(): Promise<TrustedDevice[]>;
  addTrustedDevice(device: DeviceInfo): Promise<TrustedDeviceSecret>;
  setupBiometrics(): Promise<BiometricSecret | undefined>;
  removeBiometrics(): Promise<void>;
  removeTrustedDevice(deviceId: number): Promise<void>;

  updatePhoneNumber(phoneNumber?: string): Promise<UpdateUserResponse>;
  sendVerficationCodeToPhone(phoneNumber: string): Promise<void>;
  validateCodeToAddOrUpdatePhone(verificationCode: string): Promise<UpdatePhoneOtpResponse>;
  getUserProfile(): Promise<UserProfile>;
}

export class GatewayImpl implements Gateway {
  private baseUrl = "/api";
  private get clientUrl() {
    return `${this.baseUrl}/client/${this.auth.clientId}`;
  }

  // IMPORTANT! This should be populated in main.ts
  //
  // This is being lazily initialized using a generic VueRoter instance to prevent importing @/router/index.ts, which
  // will inadvertently import every page component in the project (which is causing issues for our unit tests).
  router!: VueRouter;

  // To prevent a circular reference, the auth service should set this value during it's initialization
  auth!: Auth;

  constructor(private axios: AxiosInstance) {
    // Android devices don't have a way to configure the default timeout, so set the timeout for Axios for Android devices
    if (isAndroid()) {
      axios.defaults.timeout = 20 * 1000;
    }

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Automatically inject the auth token on API requests
    this.axios.interceptors.request.use(
      request => {
        if (this.auth.token) {
          if (!request.headers) request.headers = {};
          request.headers.Authorization = `Bearer ${this.auth.token}`;
        }
        return request;
      },
      error => Promise.reject(error)
    );

    this.axios.interceptors.response.use(
      // Automatically return the response data instead of making each method return response.data
      response => response.data,

      async error => {
        // In the case of network failure, the response may be undefined so default to an empty object
        const response = error.response || {};
        response.isHandled = await this.redirectIfNetworkErrorOrUnauthorized(error);

        // Have Axios return the response instead of the error
        throw response;
      }
    );
  }

  login(email: string, password: string, trustedDeviceSecret?: string): Promise<LoginResult> {
    const url = `${this.baseUrl}/account/login`;
    return (
      this.axios
        .post(url, {
          email,
          password,
          deviceGuid: this.auth.deviceGuid,
          deviceSecret: trustedDeviceSecret
        })
        // Catch any errors and return the result since it will contain a login result with the error message
        .catch(r => r?.data)
    );
  }

  loginWithBiometrics(email: string, secret: string): Promise<BiometricLoginResult> {
    const url = `${this.baseUrl}/account/login-biometrics`;
    return (
      this.axios
        .post(url, { email, deviceGuid: this.auth.deviceGuid, secret })
        // Catch any errors and return the result since it will contain a login result with the error message
        .catch(r => r?.data)
    );
  }

  loginWith2FA(verificationCode: string): Promise<VerifyCodeResponse> {
    const url = `${this.baseUrl}/account/login-2fa`;
    return (
      this.axios
        .post(url, { deviceGuid: this.auth.deviceGuid, verificationCode })
        // Catch any errors and return the result since it will contain a login result with the error message
        .catch(r => r?.data)
    );
  }

  requestVerificationCode(method: VerificationMethod): Promise<void> {
    const url = `${this.baseUrl}/account/request-2fa-code`;
    return this.axios.post(url, { channel: method, deviceGuid: this.auth.deviceGuid });
  }

  logout(): Promise<void> {
    const url = `${this.baseUrl}/account/logout`;
    return this.axios.post(url, {}).catch(r => r?.data);
  }

  getPasswordRules(): Promise<PasswordRules> {
    const url = `${this.baseUrl}/account/password-rules`;
    return this.axios.get(url);
  }

  changePassword(current: string, password: string, confirm: string): Promise<void> {
    const url = `${this.baseUrl}/account/change-password`;
    return this.axios.post(url, {
      currentPassword: current,
      newPassword: password,
      confirmPassword: confirm
    });
  }

  pingConnection(): Promise<void> {
    const url = `${this.baseUrl}/ping`;
    return this.axios.get(url);
  }

  getForgotPasswordUrl(): Promise<string> {
    const url = `${this.baseUrl}/settings/forgot-password-url`;
    return this.axios.get(url);
  }

  sendVerficationCodeToPhone(phoneNumber: string): Promise<void> {
    const url = `${this.baseUrl}/account/mobile-verification-code`;
    return this.axios.post(url, { deviceGuid: this.auth.deviceGuid, phoneNumber });
  }

  validateCodeToAddOrUpdatePhone(verificationCode: string): Promise<UpdatePhoneOtpResponse> {
    const url = `${this.baseUrl}/account/verify-mobile-code`;
    return this.axios.post(url, { deviceGuid: this.auth.deviceGuid, verificationCode });
  }

  updatePhoneNumber(phoneNumber?: string): Promise<UpdateUserResponse> {
    const url = `${this.baseUrl}/account/update-account`;
    return this.axios.patch(url, { phoneNumber });
  }

  updatePushInfo(pushInfo: PushNotificationInfo): Promise<void> {
    const url = `${this.baseUrl}/account/push-info`;
    return this.axios.post(url, pushInfo);
  }

  getIdnsAndClients(): Promise<IdnAndClients[]> {
    const url = `${this.baseUrl}/clients`;
    return this.axios.get(url);
  }

  getAllPatients(): Promise<PatientListViewModel[]> {
    const url = `${this.clientUrl}/patients`;
    return this.axios.get(url);
  }

  getMyPatients(): Promise<MyPatientsList> {
    const url = `${this.clientUrl}/patients/my`;
    return this.axios.get(url);
  }

  getPatient(patientId: number): Promise<PatientDetailsViewModel> {
    const url = `${this.clientUrl}/patients/${patientId}`;
    return this.axios.get(url);
  }

  subcribeToPatients(patientIds: number[], pushInfo?: PushNotificationInfo): Promise<void> {
    const url = `${this.clientUrl}/patients/subscribe`;
    return this.axios.post(url, { pushInfo: pushInfo, patientIds: patientIds });
  }

  changeClient(newClientId: number, pushInfo?: PushNotificationInfo): Promise<void> {
    const url = `${this.baseUrl}/clients/change/${newClientId}`;
    return this.axios.post(url, { pushInfo: pushInfo });
  }

  getClientSettings(clientId: number): Promise<ClientSettings> {
    const url = `${this.baseUrl}/clients/${clientId}/settings`;
    return this.axios.get(url);
  }

  getTrustedDevices(): Promise<TrustedDevice[]> {
    const url = `${this.baseUrl}/account/trusted-devices`;
    return this.axios.get(url);
  }

  addTrustedDevice(device: DeviceInfo): Promise<TrustedDeviceSecret> {
    const url = `${this.baseUrl}/account/trusted-devices`;
    return this.axios.post(url, device);
  }

  setupBiometrics(): Promise<BiometricSecret | undefined> {
    const url = `${this.baseUrl}/account/trusted-devices/${this.auth.deviceGuid}/biometrics`;
    return this.axios.post(url);
  }

  removeBiometrics(): Promise<void> {
    const url = `${this.baseUrl}/account/trusted-devices/${this.auth.deviceGuid}/biometrics`;
    return this.axios.delete(url);
  }

  removeTrustedDevice(deviceId: number): Promise<void> {
    const url = `${this.baseUrl}/account/trusted-devices/${deviceId}`;
    return this.axios.delete(url);
  }

  getUserProfile(): Promise<UserProfile> {
    const url = `${this.baseUrl}/account/profile`;
    return this.axios.get(url);
  }

  private async redirectIfNetworkErrorOrUnauthorized(error: AxiosError): Promise<boolean> {
    // If there's a network error, there's a good chance there won't be a response
    if (!error.response) {
      this.redirectToConnectionErrorPage();
      return true;
    }

    switch (error.response.status) {
      case 401:
        // If the user has a token but is receiving 401, delete the token before redirecting to the login page
        await this.auth.logout();
        this.router.push("/sign-in");
        return true;

      case 403:
        this.router.push("/unauthorized");
        return true;

      case 500:
        if (RegexConnectionError.test(error.response.data)) {
          this.redirectToConnectionErrorPage();
          return true;
        }
        break;
    }

    return false;
  }

  private redirectToConnectionErrorPage() {
    if (this.router.currentRoute.path !== "/connection-error") {
      this.router.push("/connection-error");
    }
  }
}

// Expose gateway as a singleton instance
export default new GatewayImpl(_axios) as Gateway;
